import { FC } from "react";

import { ConfirmActionModal } from "src/components/Modal";
import { useToast } from "src/components/Toasts/ToastContainer.tsx";
import { mapToModalError, useMapToMutationError } from "src/errors";
import { useActivateScenarioVersionMutation } from "src/graphql/anti-fraud/queries/scenarios/ActivateScenarioVersionMutation.ts";
import { getFullScenarioQuery } from "src/graphql/anti-fraud/queries/scenarios/GetFullScenarioQuery.ts";
import { useT } from "src/internationalization";
import { ScenarioVersion } from "src/types/scenario.ts";

type ConfirmActivateVersionModalProps = {
  draftVersion: ScenarioVersion;
  scenarioId: string;
  onSubmit: () => void;
  onClose: () => void;
};

export const ConfirmActivateVersionModal: FC<ConfirmActivateVersionModalProps> = ({
  draftVersion,
  scenarioId,
  onSubmit,
  onClose,
}) => {
  const t = useT();
  const { showSuccessToast } = useToast();
  const mapToMutationError = useMapToMutationError();

  const [
    activateScenarioVersion,
    { loading: activateScenarioVersionLoading, error: activateScenarioVersionError },
  ] = useActivateScenarioVersionMutation({
    refetchQueries: [{ query: getFullScenarioQuery, variables: { id: scenarioId } }],
    awaitRefetchQueries: true,
    onCompleted: () => {
      showSuccessToast(t("activate.scenario.version.confirmation.modal.success.toast.title"));
      onSubmit();
    },
  });

  const onConfirm = () => {
    activateScenarioVersion({ variables: { id: draftVersion.id } });
  };

  return (
    <ConfirmActionModal
      title={t("activate.scenario.version.modal.title")}
      subtitle={draftVersion.name}
      error={mapToModalError(mapToMutationError(activateScenarioVersionError))}
      confirmButtonText={t("general.actions.confirm")}
      loading={activateScenarioVersionLoading}
      onConfirm={onConfirm}
      size="sm"
      onClose={onClose}
    >
      {t("activate.scenario.version.confirmation.modal.message")}
    </ConfirmActionModal>
  );
};
